import { createEffect, createSignal, Show } from "solid-js";

import { Geocode } from "./Geocode";
import { InputField } from "./InputField";
import type { UtdoDate } from "../types";
import { toNullableFloat, toNullableInt, toNullableString, toString } from "../util/converters";

export type DatesEditorProps = {
    date: UtdoDate;
    validate: (date: UtdoDate) => any;
    cancel: () => any;
};

export function DatesEditor(props: DatesEditorProps) {
    const [ specifyCoords, setSpecifyCoords ] = createSignal(false);

    let idRef!: HTMLInputElement;
    let nameRef!: HTMLInputElement;
    let descRef!: HTMLTextAreaElement;
    let dateRef!: HTMLInputElement;
    let locationRef!: HTMLInputElement;
    let streetRef!: HTMLInputElement;
    let zipRef!: HTMLInputElement;
    let cityRef!: HTMLInputElement;
    let countryRef!: HTMLInputElement;
    let costRef!: HTMLInputElement;
    let lngRef!: HTMLInputElement;
    let latRef!: HTMLInputElement;
    let hiddenRef!: HTMLInputElement;
    let canceledRef!: HTMLInputElement;

    createEffect(() => setSpecifyCoords(props.date?.lng !== undefined && props.date?.lat !== undefined));
    
    const getCoords = (): [ number, number ] => {
        return [
            props.date?.lng ?? 2.531357315036445,
            props.date?.lat ?? 46.48120316969931,
        ];
    }

    const setCoords = (coords: [ number, number ]) => {
        setSpecifyCoords(true);
        lngRef.value = `${coords[0]}`;
        latRef.value = `${coords[1]}`;
    };

    const cancel = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        props.cancel();
    };

    const validate = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        const id = toNullableInt(idRef.value);
        const name = toString(nameRef.value);
        const description = toNullableString(descRef.value);
        const date = toString(dateRef.value);
        const location = toNullableString(locationRef.value);
        const street = toNullableString(streetRef.value);
        const zip = toNullableString(zipRef.value);
        const city = toNullableString(cityRef.value);
        const country = toNullableString(countryRef.value);
        const cost = toNullableString(costRef.value);
        let lng: number | undefined;
        let lat: number | undefined;
        if (specifyCoords()) {
            lng = toNullableFloat(lngRef.value);
            lat = toNullableFloat(latRef.value);
        }
        const hidden = hiddenRef.checked;
        const canceled = canceledRef.checked;
        props.validate({
            id,
            name,
            description,
            date,
            location,
            street,
            zip,
            city,
            country,
            cost,
            lat,
            lng,
            hidden,
            canceled,
        });
    };

    return <form class="editor" onSubmit={validate} autocomplete="off">
        <input
            ref={idRef}
            type="hidden"
            value={props.date.id}/>
        <div class="grille">
            <InputField
                ref={nameRef}
                type="text"
                id="date-name"
                label="Nom"
                required
                value={props.date.name}/>
            <InputField
                ref={descRef}
                type="textarea"
                id="date-desc"
                label="Description"
                required
                value={props.date.description ?? ""}/>
            <InputField
                ref={dateRef}
                type="date"
                id="date-date"
                label="Date"
                required
                value={props.date.date ?? ""}/>
            <InputField
                ref={locationRef}
                type="text"
                id="date-location"
                label="Lieu"
                value={props.date.location ?? ""}/>
            <InputField
                ref={streetRef}
                type="text"
                id="date-street"
                label="Rue"
                value={props.date.street ?? ""}/>
            <InputField
                ref={zipRef}
                type="text"
                id="date-zip"
                label="Code postal"
                value={props.date.zip ?? ""}/>
            <InputField
                ref={cityRef}
                type="text"
                id="date-city"
                label="Ville"
                value={props.date.city ?? ""}/>
            <InputField
                ref={countryRef}
                type="text"
                id="date-country"
                label="Pays"
                value={props.date.country ?? ""}/>
            <InputField
                ref={costRef}
                type="text"
                id="date-cost"
                label="Pays"
                value={props.date.cost ?? ""}/>
            <InputField
                type="checkbox"
                id="date-specify-coords"
                label="Spécifier les coordonnées"
                value={specifyCoords()}
                onChange={(ev) => setSpecifyCoords((ev.target as HTMLInputElement).checked)}
                />
            <Show when={specifyCoords()}>
                <InputField
                    ref={lngRef}
                    type="number"
                    step="any" min="-180" max="180"
                    id="date-lng"
                    label="Longitude"
                    value={props.date.lng ?? 0}/>
                <InputField
                    ref={latRef}
                    type="number"
                    step="any" min="-90" max="90"
                    id="date-lat"
                    label="Latitude"
                    value={props.date.lat ?? 0}/>
            </Show>
            <InputField
                ref={hiddenRef}
                type="checkbox"
                id="date-hidden"
                label="Cacher"
                value={props.date?.hidden}/>
            <InputField
                ref={canceledRef}
                type="checkbox"
                id="date-canceled"
                label="Annulé"
                value={props.date?.canceled}/>
        </div>
        <Geocode coords={getCoords()} setCoords={setCoords}/>
    <div class="actions">
        <input type="submit" value="Enregistrer" class="accent" />
        <button onClick={cancel}>Annuler</button>
    </div>
</form>
}